// The people in front of a window restaurant: each walks up the boardwalk,
// taps their order into the tablet kiosk, waits by the pickup window, and
// walks off with their basket (or without it, if they gave up). The ticket
// prints the moment they finish at the kiosk, exactly when it would have
// without them, so they add something to look at without changing a day.
//
// A customer is a few times; where one stands at any moment is worked out
// from them (`customerPose`), so the sim stays small and the render just
// draws what this says. Their look comes from their order number, not the
// day's random numbers, for the same reason.

/** Seconds tapping at the kiosk before the ticket prints. */
export const KIOSK_SECONDS = 4;
/** Walking speed outside, metres a second. */
export const STROLL = 1.3;
/** Seconds stepping up to the window to take a basket. */
export const COLLECT_SECONDS = 1.2;
/** Seconds walking away before they're gone. */
export const LEAVE_SECONDS = 12;

export interface Customer {
  /** The order number they'll get (their ticket's `no`). */
  no: number;
  /** When they set off up the boardwalk. */
  comesAt: number;
  /** When they finish at the kiosk and the ticket prints. */
  orderAt: number;
  /** When they got their order, or gave up. */
  doneAt?: number;
  outcome?: "served" | "walkout";
}

/** Places on the pad in front of the Burger Shack, in kitchen metres (the front wall is at z 4.4). */
export const FRONT = {
  /** Where people walk on from, either end of the boardwalk. */
  ends: [
    { x: 16, z: 7.6 },
    { x: -9, z: 7.4 },
  ],
  /** Standing at the tablet kiosk, facing it. */
  kiosk: { x: 5.35, z: 5.25 },
  /** The kiosk itself. */
  kioskStand: { x: 5.35, z: 4.72 },
  /** Right at the pickup window, to take a basket. */
  window: { x: 3.85, z: 4.85 },
  /** Where people wait for their order, nearest the window first. */
  waiting: [
    { x: 3.3, z: 6.4 },
    { x: 4.6, z: 6.5 },
    { x: 2.1, z: 6.6 },
    { x: 5.7, z: 6.8 },
    { x: 3.9, z: 7.2 },
    { x: 1.2, z: 7.0 },
    { x: 2.8, z: 7.5 },
    { x: 5.0, z: 7.6 },
  ],
} as const;

type Point = { x: number; z: number };

/** Seconds to walk from a boardwalk end to the kiosk. */
export function walkInSeconds(no: number): number {
  return dist(endFor(no), FRONT.kiosk) / STROLL;
}

/** How long before their ticket prints a customer appears. */
export function leadSeconds(no: number): number {
  return walkInSeconds(no) + KIOSK_SECONDS;
}

/** Which end of the boardwalk they come from (and leave by the other). */
function endFor(no: number): Point {
  return FRONT.ends[hash(no) % 2]!;
}

/** What someone looks like, from their number: shirt, skin, hair and height, as fractions. */
export function customerLook(no: number): { shirt: number; skin: number; hair: number; height: number } {
  const h = hash(no);
  return { shirt: (h % 97) / 97, skin: ((h >> 7) % 89) / 89, hair: ((h >> 13) % 83) / 83, height: ((h >> 19) % 79) / 79 };
}

export type CustomerAction = "walk" | "tap" | "wait" | "collect" | "leave";

export interface CustomerPose {
  x: number;
  z: number;
  /** Which way they face, as an angle: 0 faces -z (towards the shack). */
  facing: number;
  action: CustomerAction;
  /** Holding their basket. */
  carrying: boolean;
  /** Leaving unhappy. */
  upset: boolean;
}

/**
 * Where a customer is at `t` and what they're doing. `waitingSpot` is their
 * place in the line of people waiting (0 nearest the window).
 */
export function customerPose(c: Customer, t: number, waitingSpot: number): CustomerPose {
  const from = endFor(c.no);
  const away = FRONT.ends[(hash(c.no) + 1) % 2]!;
  const tapFrom = c.orderAt - KIOSK_SECONDS;
  const spot = FRONT.waiting[Math.min(waitingSpot, FRONT.waiting.length - 1)]!;
  const still = (p: Point, facing: number, action: CustomerAction): CustomerPose => ({ ...p, facing, action, carrying: false, upset: false });

  if (c.doneAt === undefined || t < c.doneAt) {
    if (t < tapFrom) return walking(from, FRONT.kiosk, (t - (tapFrom - walkInSeconds(c.no))) / walkInSeconds(c.no), "walk", false, false);
    if (t < c.orderAt) return still(FRONT.kiosk, 0, "tap");
    // Over to wait by the window.
    const over = dist(FRONT.kiosk, spot) / STROLL;
    if (t < c.orderAt + over) return walking(FRONT.kiosk, spot, (t - c.orderAt) / over, "walk", false, false);
    return still(spot, 0, "wait");
  }
  const since = t - c.doneAt;
  if (c.outcome === "served") {
    if (since < COLLECT_SECONDS) return { ...FRONT.window, facing: 0, action: "collect", carrying: since > COLLECT_SECONDS / 2, upset: false };
    return walking(FRONT.window, away, (since - COLLECT_SECONDS) / (LEAVE_SECONDS - COLLECT_SECONDS), "leave", true, false);
  }
  return walking(spot, away, since / LEAVE_SECONDS, "leave", false, true);
}

/** Whether a customer has walked off for good and can be forgotten. */
export function customerGone(c: Customer, t: number): boolean {
  return c.doneAt !== undefined && t >= c.doneAt + LEAVE_SECONDS;
}

function walking(a: Point, b: Point, k: number, action: CustomerAction, carrying: boolean, upset: boolean): CustomerPose {
  const u = Math.max(0, Math.min(1, k));
  return { x: a.x + (b.x - a.x) * u, z: a.z + (b.z - a.z) * u, facing: Math.atan2(-(b.x - a.x), -(b.z - a.z)), action, carrying, upset };
}

function dist(a: Point, b: Point): number {
  return Math.hypot(a.x - b.x, a.z - b.z);
}

/** A small integer hash, so a number always maps to the same look and path. */
function hash(n: number): number {
  let h = Math.imul(n ^ 0x9e3779b9, 0x85ebca6b);
  h ^= h >>> 13;
  h = Math.imul(h, 0xc2b2ae35);
  h ^= h >>> 16;
  return h >>> 0;
}
